import React, { useState } from 'react';
import type { TFunction } from 'i18next';
import type { U3203LineDiffEntry } from '../../utils/u3203LineDiff';
import { u3203ComputeLineDiff, u3203TextsDiffer } from '../../utils/u3203LineDiff';
import type { U3203AiMissionRevision } from '../../U3203Service';

interface U3203AiMissionRevisionDiffProps {
  revisionA: U3203AiMissionRevision | null;
  revisionB: U3203AiMissionRevision | null;
  loading: boolean;
  t: TFunction;
  NS: string;
}

function diffLineClass(type: U3203LineDiffEntry['type']): string {
  switch (type) {
    case 'added':
      return 'bg-green-50 text-green-900';
    case 'removed':
      return 'bg-red-50 text-red-900';
    default:
      return 'bg-gray-50 text-gray-700';
  }
}

function DiffBlock({ title, entries }: { title: string; entries: U3203LineDiffEntry[] }) {
  if (entries.length === 0) {
    return null;
  }
  const hasChanges = entries.some((e) => e.type !== 'unchanged');
  if (!hasChanges) {
    return null;
  }
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      <pre className="max-h-64 overflow-auto rounded-md border border-gray-200 font-mono text-xs">
        {entries.map((entry, idx) => (
          <div key={`${entry.type}-${idx}`} className={`whitespace-pre-wrap px-2 py-0.5 ${diffLineClass(entry.type)}`}>
            {entry.type === 'added' ? '+ ' : entry.type === 'removed' ? '- ' : '  '}
            {entry.line}
          </div>
        ))}
      </pre>
    </div>
  );
}

function formatProviderModelRef(
  provider: U3203AiMissionRevision['defaultProvider'],
  model: U3203AiMissionRevision['defaultModel'],
): string {
  const providerLabel = provider ? `${provider.name} (${provider.code})` : '—';
  const modelLabel = model ? `${model.name} (${model.code})` : '—';
  return `${providerLabel} / ${modelLabel}`;
}

function parameterOverridesDiffer(
  a: Record<string, unknown> | undefined,
  b: Record<string, unknown> | undefined,
): boolean {
  return JSON.stringify(a ?? {}) !== JSON.stringify(b ?? {});
}

const U3203AiMissionRevisionDiff: React.FC<U3203AiMissionRevisionDiffProps> = ({
  revisionA,
  revisionB,
  loading,
  t,
  NS,
}) => {
  const [showUserPrompt, setShowUserPrompt] = useState(false);
  const [showMetadata, setShowMetadata] = useState(true);

  if (loading) {
    return <p className="text-sm text-gray-600">{t('msgLoading', { ns: NS })}</p>;
  }

  if (!revisionA || !revisionB) {
    return <p className="text-sm text-gray-600">{t('msgSelectTwoRevisions', { ns: NS })}</p>;
  }

  const systemDiff = u3203ComputeLineDiff(revisionA.systemPrompt, revisionB.systemPrompt);
  const userDiff = u3203ComputeLineDiff(revisionA.userPromptTemplate ?? '', revisionB.userPromptTemplate ?? '');
  const userPromptDiffers = u3203TextsDiffer(revisionA.userPromptTemplate, revisionB.userPromptTemplate);
  const systemHasChanges = systemDiff.some((e) => e.type !== 'unchanged');
  const userHasChanges = userDiff.some((e) => e.type !== 'unchanged');

  const providerModelA = formatProviderModelRef(revisionA.defaultProvider, revisionA.defaultModel);
  const providerModelB = formatProviderModelRef(revisionB.defaultProvider, revisionB.defaultModel);
  const providerModelDiffers = providerModelA !== providerModelB;

  const overridesA = JSON.stringify(revisionA.parameterOverrides ?? {}, null, 2);
  const overridesB = JSON.stringify(revisionB.parameterOverrides ?? {}, null, 2);
  const overridesDiff = u3203ComputeLineDiff(overridesA, overridesB);
  const overridesDiffer = parameterOverridesDiffer(revisionA.parameterOverrides, revisionB.parameterOverrides);
  const overridesHasChanges = overridesDiff.some((e) => e.type !== 'unchanged');

  const hasAnyChange =
    systemHasChanges || userHasChanges || providerModelDiffers || overridesDiffer;

  if (!hasAnyChange) {
    return <p className="text-sm text-gray-600">{t('msgNoDiff', { ns: NS })}</p>;
  }

  return (
    <div className="space-y-4">
      {(providerModelDiffers || overridesDiffer) && (
        <div className="space-y-2">
          <button
            type="button"
            className="text-sm font-medium text-primary hover:underline"
            onClick={() => setShowMetadata((v) => !v)}
          >
            {showMetadata ? '▼' : '▶'} {t('lblDiffMetadata', { ns: NS })}
          </button>
          {showMetadata ? (
            <div className="space-y-4">
              {providerModelDiffers ? (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900">{t('lblDiffProviderModel', { ns: NS })}</h4>
                  <pre className="max-h-32 overflow-auto rounded-md border border-gray-200 font-mono text-xs">
                    <div className="whitespace-pre-wrap bg-red-50 px-2 py-0.5 text-red-900">- {providerModelA}</div>
                    <div className="whitespace-pre-wrap bg-green-50 px-2 py-0.5 text-green-900">+ {providerModelB}</div>
                  </pre>
                </div>
              ) : null}
              {overridesHasChanges ? (
                <DiffBlock title={t('lblDiffParameterOverrides', { ns: NS })} entries={overridesDiff} />
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      <DiffBlock title={t('lblDiffSystemPrompt', { ns: NS })} entries={systemDiff} />
      {userPromptDiffers ? (
        <div className="space-y-2">
          <button
            type="button"
            className="text-sm font-medium text-primary hover:underline"
            onClick={() => setShowUserPrompt((v) => !v)}
          >
            {showUserPrompt ? '▼' : '▶'} {t('lblDiffUserPromptTemplate', { ns: NS })}
          </button>
          {showUserPrompt ? <DiffBlock title={t('lblDiffUserPromptTemplate', { ns: NS })} entries={userDiff} /> : null}
        </div>
      ) : null}
    </div>
  );
};

export default U3203AiMissionRevisionDiff;
